import { OjWebElement } from '@oracle/oraclejet-webdriver/elements';

/**
 * This is the base class for oj-c-popup WebElement, and is generated from the
 * component's metadata. Do not modify these contents since they'll be replaced
 * during the next generation.
 * Put overrides into the WebElements's subclass, PopupWebElement.ts.
 */
export class PopupWebElementBase extends OjWebElement {
  /**
   * Gets the value of <code>opened</code> property.
   * Specifies whether the Popup is open.
   * @return The value of <code>opened</code> property.
   *
   */
  public getOpened(): Promise<boolean> {
    return this.getProperty<boolean>('opened');
  }

  /**
   * Gets the value of <code>launcher</code> property.
   * Specifies Popup's launcher. After Popup closes, it returns focus to the launcher.
   * @return The value of <code>launcher</code> property.
   *
   */
  public getLauncher(): Promise<string> {
    return this.getProperty<string>('launcher');
  }

  /**
   * Gets the value of <code>anchor</code> property.
   * Specifies Popup's anchor. Popup is placed relative to its anchor.
   * @return The value of <code>anchor</code> property.
   *
   */
  public getAnchor(): Promise<string | object> {
    return this.getProperty<string | object>('anchor');
  }

  /**
   * Gets the value of <code>placement</code> property.
   * Specifies the location the popup will appear relative to another element.
   * @return The value of <code>placement</code> property.
   *
   */
  public getPlacement(): Promise<string> {
    return this.getProperty<string>('placement');
  }

  /**
   * Gets the value of <code>modality</code> property.
   * Specifies modality of the Popup.
   * @return The value of <code>modality</code> property.
   *
   */
  public getModality(): Promise<string> {
    return this.getProperty<string>('modality');
  }

  /**
   * Gets the value of <code>autoDismiss</code> property.
   * Specifies the auto dismissal behavior.
   * @return The value of <code>autoDismiss</code> property.
   *
   */
  public getAutoDismiss(): Promise<string> {
    return this.getProperty<string>('autoDismiss');
  }

  /**
   * Gets the value of <code>tail</code> property.
   * Specifies Popup's tail. Simple tail is an arrow pointing to Popup's anchor.
   * @return The value of <code>tail</code> property.
   *
   */
  public getTail(): Promise<string> {
    return this.getProperty<string>('tail');
  }

  /**
   * Gets the value of <code>variant</code> property.
   * Specifies Popup's style variant.
   * @return The value of <code>variant</code> property.
   *
   */
  public getVariant(): Promise<string> {
    return this.getProperty<string>('variant');
  }

  /**
   * Gets the value of <code>initialFocus</code> property.
   * Specifies if the Popup sets focus to its content when initially open.
   * @return The value of <code>initialFocus</code> property.
   *
   */
  public getInitialFocus(): Promise<string> {
    return this.getProperty<string>('initialFocus');
  }

  /**
   * Gets the value of <code>offset</code> property.
   * Specifies displacement of the Popup from the anchor element along the specified axes.
   * @return The value of <code>offset</code> property.
   *
   */
  public getOffset(): Promise<Offset> {
    return this.getProperty<Offset>('offset');
  }

  /**
   * Gets the value of <code>collision</code> property.
   * Specifies rule for alternate placement alignment.
   * @return The value of <code>collision</code> property.
   *
   */
  public getCollision(): Promise<string> {
    return this.getProperty<string>('collision');
  }

  /**
   * Gets the value of <code>width</code> property.
   * Specifies width of the Popup content.
   * @return The value of <code>width</code> property.
   *
   */
  public getWidth(): Promise<number | string> {
    return this.getProperty<number | string>('width');
  }

  /**
   * Gets the value of <code>minWidth</code> property.
   * Specifies minWidth of the Popup content.
   * @return The value of <code>minWidth</code> property.
   *
   */
  public getMinWidth(): Promise<number | string> {
    return this.getProperty<number | string>('minWidth');
  }

  /**
   * Gets the value of <code>maxWidth</code> property.
   * Specifies maxWidth of the Popup content.
   * @return The value of <code>maxWidth</code> property.
   *
   */
  public getMaxWidth(): Promise<number | string> {
    return this.getProperty<number | string>('maxWidth');
  }

  /**
   * Gets the value of <code>height</code> property.
   * Specifies height of the Popup content.
   * @return The value of <code>height</code> property.
   *
   */
  public getHeight(): Promise<number | string> {
    return this.getProperty<number | string>('height');
  }

  /**
   * Gets the value of <code>minHeight</code> property.
   * Specifies minHeight of the Popup content.
   * @return The value of <code>minHeight</code> property.
   *
   */
  public getMinHeight(): Promise<number | string> {
    return this.getProperty<number | string>('minHeight');
  }

  /**
   * Gets the value of <code>maxHeight</code> property.
   * Specifies maxHeight of the Popup content.
   * @return The value of <code>maxHeight</code> property.
   *
   */
  public getMaxHeight(): Promise<number | string> {
    return this.getProperty<number | string>('maxHeight');
  }

  /**
   * Gets the value of <code>backgroundColor</code> property.
   * Specifies background color of the Popup.
   * @return The value of <code>backgroundColor</code> property.
   *
   */
  public getBackgroundColor(): Promise<string> {
    return this.getProperty<string>('backgroundColor');
  }
}
export interface Offset {
  /**
   *
   */
  x: number;
  /**
   *
   */
  y: number;
}
